<template>
  <div class="page page-with-bg">
    <div class="page-form-wrap">
      <div class="page-form-header small-header">
        <h1>Order Placed</h1>
      </div>
      <div class="page-form-content">
        <b-form-group>
          Your order <span class="text-success">#{{ report.id }}</span> was placed for <span class="text-success">{{ report.patientName }}</span> for a <span class="text-success">{{ report.type }}</span>
        </b-form-group>
        <b-form-group class="dark-text" v-if="report.status === ReportStatus.OrderLabsFirst">
          <h5 class="form-heading">What's next?</h5>
          <p>
            We will now prepare the lab order through LabCorp for your patient.
          </p>
          <p>
            Your patient will be notified that their labs have been ordered and we <br />
            will communicate the next steps for having their blood sample drawn.
          </p>
          <p>
            After we complete the lab requisition, when you are ready for the <br />
            patient to complete the Preassessment Questionnaire, you will need <br />
            to log in and select that Assessment from your Reports tab to send <br />
            the Preassessment Questionnaire email to your patient.
          </p>
        </b-form-group>
        <b-form-group v-else>
          <h5 class="form-heading">What's next?</h5>
          <ul v-if="report.labworkType === LabworkType.Internal" class="form-listing">
            <li>
              We will send them an email with a link to complete or review their personal and clinical
              history information. This link is specific to this order and should not be shared with others.
            </li>
            <li>
              Once they complete the survey link that we send, we will email them further
              instructions to complete their blood draw at their local Labcorp facility.

              <p class="mt-5">
                You can track the status of all of your open orders on the
                <router-link class="text-success" to="..">Reports</router-link> page of your account
              </p>
            </li>
          </ul>
          <ul v-if="report.labworkType === LabworkType.ByClient" class="form-listing">
            <li>
              We will send them an email with a link to complete or review their personal and clinical
              history information. This link is specific to this order and should not be shared with others.
            </li>
            <li>
              Once they complete the survey link that we send, we will wait to receive their
              LabCorp results from your clinic. You can upload to results under
              <router-link class="text-success" to="/information/client-resources">Client Resources</router-link>.
              We will also periodically look for the results in LabCorp Link.

              <p class="mt-5">
                You can track the status of all of your open orders on the
                <router-link class="text-success" to="..">Reports</router-link> page of your account
              </p>
            </li>
          </ul>
          <ul v-if="report.labworkType === LabworkType.ClientDraw" class="form-listing">
            <li>
              We will send your tested individual an email with a link to complete or review their personal and clinical
              history information. This link is specific to this order and should not be shared with others.
            </li>
            <li>
              Once the tested individual completes their Preassessment Questionnaire survey, we will upload their
              LabCorp lab req to their order in the portal and you can download it from the Reports tab. Once the
              labs are drawn and processed at LabCorp, we will process the final report and post it to the Reports tab.

              <p class="mt-5">
                You can track the status of all of your open orders on the
                <router-link class="text-success" to="..">Reports</router-link> page of your account
              </p>
            </li>
          </ul>
          <ul v-if="report.labworkType === LabworkType.QuestPSC" class="form-listing">
            <li>
              We will send your tested individual an email with a link to complete or review their personal and clinical
              history information. This link is specific to this order and should not be shared with others.
            </li>
            <li>
              Once your tested individual completes the survey link that we send, we will email them further
              instructions to complete their blood draw at their local Quest facility.

              <p class="mt-5">
                You can track the status of all of your open orders on the
                <router-link class="text-success" to="..">Reports</router-link> page of your account
              </p>
            </li>
          </ul>
          <ul v-if="report.labworkType === LabworkType.QuestClientDraw" class="form-listing">
            <li>
              We will send your tested individual an email with a link to complete or review their personal and clinical
              history information. This link is specific to this order and should not be shared with others.
            </li>
            <li>
              Once the tested individual completes their Preassessment Questionnaire survey, we will upload their
              Quest lab req to their order in the portal and you can download it from the Reports tab. Once the
              labs are drawn and processed at Quest, we will process the final report and post it to the Reports tab.

              <p class="mt-5">
                You can track the status of all of your open orders on the
                <router-link class="text-success" to="..">Reports</router-link> page of your account
              </p>
            </li>
          </ul>
          <ul v-if="report.labworkType === LabworkType.QuestByClient" class="form-listing">
            <li>
              We will send your tested individual an email with a link to complete or review their personal and clinical
              history information. This link is specific to this order and should not be shared with others.
            </li>
            <li>
              Once your tested individual completes the survey link that we send, we will wait to receive their
              lab results from your clinic. You can upload the results under

              <router-link class="text-success" to="/information/client-resources">Client Resources</router-link>.
              We will also periodically look for the results in Quest Quantum.
              <p class="mt-5">
                You can track the status of all of your open orders on the
                <router-link class="text-success" to="..">Reports</router-link> page of your account
              </p>
            </li>
          </ul>
          <ul v-if="report.labworkType === LabworkType.ByClientI" class="form-listing">
            <li>
              We will send your tested individual an email with a link to complete or review their personal and clinical
              history information. This link is specific to this order and should not be shared with others.
            </li>
            <li>
              You will be responsible for collecting labs and ensuring delivery of the lab results to Precision Health Reports for final Assessment processing.

              <p class="mt-5">
                You can track the status of all of your open orders on the
                <router-link class="text-success" to="..">Reports</router-link> page of your account
              </p>
            </li>
          </ul>
        </b-form-group>
        <b-form-group class="form-buttons mb-4">
          <b-btn variant="light" to="/">Home</b-btn>
          <b-btn variant="success" @click="createOrder()">Order Another Analysis</b-btn>
        </b-form-group>
        <template v-if="canCompletePatientSurvey">
          <h5 class="form-heading">Other Options</h5>
          <b-btn variant="success" class="other-button" @click="completePatientSurvey()">Complete the Patient Survey</b-btn>
          <b-btn variant="success" class="other-button my-2" @click="completePatientSurveyAsClient()">Make Survey Available to the Individual To Take On This Device</b-btn>
          <div>
            <b>Note:</b> to Protect your other patient's privacy, the latter option will <br />
            require you to log back in after the survey is complete/saved
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
  import { createNamespacedHelpers } from 'vuex';
  const { mapState: mapReportState, mapGetters: mapReportGetters } = createNamespacedHelpers('report');
  const { mapActions: mapUserActions } = createNamespacedHelpers('user');

  import ReportStatus from "enums/report-status";
  import LabworkType from "enums/labwork-type";

  export default {
    data() {
      return {
        ReportStatus,
        LabworkType
      }
    },

    computed: {
      ...mapReportState(["report"]),
      ...mapReportGetters(["patientName"]),

      isAdmin() {
        return this.$auth.isInRole("Admin");
      },

      canCompletePatientSurvey() {
        return (this.$auth.isInRole("Client") ||
          this.$auth.isInRole("ClientProvider") ||
          this.$auth.isInRole("ClientUser") ||
          this.$auth.isInRole("ClientAdmin")) &&
          this.report.status !== ReportStatus.OrderLabsFirst;
      }
    },

    methods: {
      ...mapUserActions(["loginAsUser"]),

      createOrder() {
        if (this.isAdmin) {
          this.$router.push({ path: "/reports/create", query: { id: this.report.clientId } });
        }
        else {
          this.$router.push("/reports/create");
        }
      },

      async completePatientSurvey() {
        let user = {
          email: this.report.patientEmail,
        };

        await this.loginAsUser({
          user: user,
          clientRedirect: true
        });
      },

      async completePatientSurveyAsClient() {
        let user = {
          email: this.report.patientEmail,
        };

        await this.loginAsUser({
          user: user,
          clientRedirect: true,
          grantType: "client_as_patient"
        });
      }
    }
  };
</script>
<style lang="scss" scoped>
  @import '@/styles/mixins';

  .page {
    @include retina('/png/doctor.png');
  }

  .other-button {
    width: 21.5rem;
  }

  .dark-text {
    p {
      color: #333;
    }
  }

  .yellow {
    color: #ffc000;
  }

  .red {
    color: #c04f15;
  }
</style>